import type { NextRouter } from 'next/router';

export const AUTH_CALLBACK_COOKIE = 'mynawoo_auth_callback';
export const AUTH_CALLBACK_MAX_AGE = 30 * 60;

export function isSafeCallbackUrl(value: string) {
  return value.startsWith('/') && !value.startsWith('//') && !value.includes('\n') && !value.includes('\r');
}

export function callbackFromRequestPath(pathname: string, search = '') {
  const path = `${pathname}${search}`;
  if (!path || path === '/' || pathname.startsWith('/auth')) return '';
  return isSafeCallbackUrl(path) ? path : '';
}

export function callbackFromAsPath(asPath: string) {
  const q = asPath.indexOf('?');
  const pathname = q === -1 ? asPath : asPath.slice(0, q);
  const search = q === -1 ? '' : asPath.slice(q);
  return callbackFromRequestPath(pathname, search);
}

export function buildAuthRedirect(baseUrl: string | URL, authPage: string, callbackUrl: string) {
  const url = new URL(authPage, baseUrl);
  if (callbackUrl && callbackUrl !== '/') {
    url.searchParams.set('callbackUrl', callbackUrl);
  }
  return url;
}

export function loginPathFromAsPath(asPath: string) {
  return authPath('/auth/login', callbackFromAsPath(asPath) || '/');
}

export function postAuthDestination(callbackUrl: string, welcomeFallback = '/classroom') {
  if (callbackUrl && callbackUrl !== '/' && !callbackUrl.startsWith('/auth')) {
    return callbackUrl;
  }
  return welcomeFallback;
}

export function getAuthCallbackUrl(router?: NextRouter, fallback = '/') {
  const queryCallback = router?.query?.callbackUrl;
  const queryNext = router?.query?.next;
  const fromQuery = typeof queryCallback === 'string' ? queryCallback : (typeof queryNext === 'string' ? queryNext : '');
  if (fromQuery && isSafeCallbackUrl(fromQuery)) return fromQuery;

  if (typeof document !== 'undefined') {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${AUTH_CALLBACK_COOKIE}=`));
    if (cookie) {
      const value = decodeURIComponent(cookie.split('=').slice(1).join('='));
      if (isSafeCallbackUrl(value)) return value;
    }
  }

  return fallback;
}

export function persistAuthCallbackUrl(callbackUrl: string) {
  if (typeof document === 'undefined' || !isSafeCallbackUrl(callbackUrl) || callbackUrl === '/' || callbackUrl.startsWith('/auth')) return;
  document.cookie = `${AUTH_CALLBACK_COOKIE}=${encodeURIComponent(callbackUrl)}; path=/; max-age=${AUTH_CALLBACK_MAX_AGE}; SameSite=Lax`;
}

export function clearAuthCallbackUrl() {
  if (typeof document === 'undefined') return;
  document.cookie = `${AUTH_CALLBACK_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export function authPath(path: string, callbackUrl: string) {
  if (!callbackUrl || callbackUrl === '/') return path;
  return `${path}?callbackUrl=${encodeURIComponent(callbackUrl)}`;
}
